// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import {
  type Column,
  type OnChangeFn,
  type VisibilityState,
} from '@tanstack/react-table';
import { PopupSelector } from '@shared/ui-kit/components';
import { useStateValue } from '@shared/ui-kit/providers/state-provider';
import { IconButton } from '@shared/ui-kit/components/icon-button';
import { type ButtonHTMLAttributes, forwardRef, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

export interface ColumnVisibilityProps
  extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'children'> {
  readonly columns: Column<any, any>[];
  readonly onColumnVisibilityChange: OnChangeFn<VisibilityState>;
  readonly ['state-id']?: string;
}

export const ColumnVisibilitySelector = forwardRef<
  HTMLButtonElement,
  ColumnVisibilityProps
>(
  (
    { columns, onColumnVisibilityChange, 'state-id': stateId = 'grouped-table' },
    ref,
  ) => {
    const { t } = useTranslation();
    const [, setState] = useStateValue<VisibilityState>(stateId);

    const visibleColumns = useMemo(
      () =>
        columns.filter(column => column.getIsVisible()).map(column => column.id),
      [columns],
    );

    const options = useMemo(
      () =>
        columns
          .filter(
            column =>
              column.id !== 'options' &&
              column.getCanHide() &&
              typeof column.columnDef.header === 'string', // ignore columns without a header
          )
          .map(column => ({
            value: column.id,
            label: (column.columnDef.header as string).toLocaleUpperCase(),
          })),
      [columns],
    );

    const handleSaveColumns = useCallback(
      (selectedColumns: string[]) => {
        const visibilityState = options
          .filter(column => !selectedColumns.includes(column.value))
          .reduce((acc, column) => {
            acc[column.value] = false;
            return acc;
          }, {} as VisibilityState);

        onColumnVisibilityChange(visibilityState);

        setState(visibilityState);
      },
      [onColumnVisibilityChange, options, setState],
    );

    return (
      <PopupSelector
        title={t('common.edit-columns', { ns: 'common' })}
        options={options}
        initialSelectedOptions={visibleColumns}
        onSave={handleSaveColumns}
      >
        <IconButton
          variant="ghost"
          icon="Settings"
          ref={ref}
          aria-label="Column visibility"
        />
      </PopupSelector>
    );
  },
);

ColumnVisibilitySelector.displayName = 'ColumnVisibility';
